// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Party planner + Catch Pass  (the catch list both builders use)
// A controlled list of every catch on the menu: photo, per-kg price, a cut selector (that catch's real cuts, prep fee shown) and a
// stepper per pack size (500 g / 1 kg). The parent owns `picks` (utils/picks) and decides what to do with them (add to cart, save as a
// plan). Nothing is stored here. Neighbours: data/seafood, data/cuts, data/packs, utils/picks.
// ─────────────────────────────────────────────────────────────────────────────
import { Minus, Plus } from "lucide-react";
import { cutsFor, cutOf } from "../../data/cuts";
import { PACK_SIZES, packPrice } from "../../data/packs";
import { seafoodItems } from "../../data/seafood";
import { MAX_PER_SIZE, pickOf, pickPrice, type Picks } from "../../utils/picks";
import { formatInr } from "../../utils/priceFormat";
import { getWikimediaSrcSet } from "../../utils/wikimediaImage";

interface CatchPickerProps {
  picks: Picks;
  onChange: (picks: Picks) => void;
  /** Prefix for element ids when two pickers could be on one page. */
  idPrefix?: string;
}

export default function CatchPicker({ picks, onChange, idPrefix = "pick" }: CatchPickerProps) {
  return (
    <ul className="flex flex-col gap-3">
      {seafoodItems.map((item) => {
        const pick = pickOf(picks, item);
        const cut = cutOf(item, pick.cut);
        const set = (next: typeof pick) => onChange({ ...picks, [item.id]: next });
        const on = pick["500g"] + pick["1kg"] > 0;
        return (
          <li key={item.id} className={`rounded-2xl p-3 shadow-sm ring-1 ${on ? "bg-teal-100/40 ring-teal-600/40 dark:bg-teal-700/10" : "bg-white ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10"}`} data-testid="pick-row">
            <div className="flex gap-3">
              <img src={item.image} srcSet={getWikimediaSrcSet(item.image)} sizes="64px" alt="" width={64} height={64} loading="lazy" title={item.credit} className="h-16 w-16 shrink-0 rounded-xl object-cover" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-ocean-950 dark:text-foam-50">{item.name}</p>
                <p className="text-xs text-ocean-800/80 dark:text-foam-100/75">{item.price} {item.unit}</p>
                {on && <p className="mt-0.5 text-xs font-bold text-teal-700 dark:text-teal-300">{formatInr(pickPrice(item, pick))}</p>}
              </div>
            </div>
            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto]">
              <div>
                <label htmlFor={`${idPrefix}-${item.id}-cut`} className="text-[11px] font-bold uppercase tracking-wide text-ocean-800 dark:text-foam-100">Cut for {item.name}</label>
                <select
                  id={`${idPrefix}-${item.id}-cut`}
                  value={cut.id}
                  onChange={(event) => set({ ...pick, cut: event.target.value as typeof pick.cut })}
                  className="mt-0.5 min-h-11 w-full rounded-xl bg-white px-2 text-sm text-ocean-950 ring-1 ring-ocean-900/15 focus-visible:outline-2 focus-visible:outline-teal-600 dark:bg-ocean-950 dark:text-foam-50"
                >
                  {cutsFor(item).map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                      {option.prepFeePerKg > 0 ? ` (+₹${option.prepFeePerKg}/kg)` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end gap-2">
                {PACK_SIZES.map((size) => (
                  <div key={size.id} role="group" aria-label={`${size.label} packs of ${item.name}`} className="flex flex-col items-center">
                    <span className="text-[11px] font-bold text-ocean-800 dark:text-foam-100">{size.label} · {formatInr(packPrice(item, size) + Math.round(cut.prepFeePerKg * size.kg))}</span>
                    <div className="mt-0.5 flex items-center gap-1 rounded-full bg-ocean-700/10 p-0.5 dark:bg-white/10">
                      <button type="button" aria-label={`Fewer ${size.label} packs of ${item.name}`} disabled={pick[size.id] === 0} onClick={() => set({ ...pick, [size.id]: pick[size.id] - 1 })} className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-ocean-900 disabled:opacity-40 dark:bg-ocean-900 dark:text-foam-50">
                        <Minus className="h-4 w-4" aria-hidden="true" />
                      </button>
                      <span aria-live="polite" className="w-6 text-center text-sm font-bold text-ocean-950 dark:text-foam-50">{pick[size.id]}</span>
                      <button type="button" aria-label={`More ${size.label} packs of ${item.name}`} disabled={pick[size.id] >= MAX_PER_SIZE} onClick={() => set({ ...pick, [size.id]: pick[size.id] + 1 })} className="flex h-9 w-9 items-center justify-center rounded-full bg-ocean-800 text-white disabled:opacity-40">
                        <Plus className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
